import { FC, useEffect, ReactNode, useState } from "react";
import { useAuthStore } from "@/core/stores/auth.store";
import { notifications } from "@mantine/notifications";
import { env } from "@/config/env";
import { Avatar, Center, Group, Text, Button } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";

interface SSEProviderProps {
    children: ReactNode;
}

const SSEProvider: FC<SSEProviderProps> = ({ children }) => {
    const { authState } = useAuthStore();
    const token = authState.accessToken;
    const queryClient = useQueryClient();

    const [permission, setPermission] = useState<NotificationPermission>(
        "Notification" in window ? Notification.permission : "granted"
    );

    useEffect(() => {
        if (token && "Notification" in window && permission === "default") {
            Notification.requestPermission().then(setPermission);
        }
    }, [token, permission]);

    useEffect(() => {
        if (!token || permission !== "granted") return;

        const showBrowserNotification = (title: string, message: string) => {
            if (!("Notification" in window)) return;

            if (Notification.permission === "granted") {
                try {
                    const notification = new window.Notification(title, {
                        body: message,
                        icon: "/favicon.ico",
                    });

                    notification.onclick = () => {
                        window.focus();
                        notification.close();
                    };
                } catch (err) {
                    console.error("Failed to show browser notification", err);
                }
            }
        };

        const evtSource = new EventSource(`${env.baseUrl}/sse/notifications/?token=${token}`);

        const handleSSEEvent = (event: MessageEvent, eventName: string, defaultTitle: string, color: string) => {
            try {
                const data = JSON.parse(event.data);
                console.log(`${eventName}:`, data);

                const title = data.title || defaultTitle;
                const message = data.message || JSON.stringify(data);

                if (data.doctype === 'PERF' && data.usrFullName) {
                    const initials = data.usrFullName
                        .split(" ").filter(Boolean).map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
                    notifications.show({
                        title,
                        message: (
                            <Group gap={8} align="center" mt={4} wrap="nowrap">
                                <Avatar src={data.usrFileAvatar ?? null} size={32} radius="xl" color="yellow">
                                    {initials}
                                </Avatar>
                                <Text size="xs">{message}</Text>
                            </Group>
                        ),
                        color: 'yellow',
                        autoClose: 8000,
                    });
                } else {
                    notifications.show({
                        title,
                        message,
                        color,
                        autoClose: false,
                    });
                }

                showBrowserNotification(title, message);

                // Invalidate queries for this doctype
                if (data.doctype) {
                    queryClient.invalidateQueries({ queryKey: ['master', data.doctype] });
                    queryClient.invalidateQueries({ queryKey: [data.doctype] });
                    queryClient.invalidateQueries({ queryKey: ['masterListRowCount', data.doctype] });
                }
            } catch (e) {
                console.error(`Failed to parse SSE ${eventName}`, e);
            }
        };

        // Listen for standard message event streams
        evtSource.onmessage = (event) => {
            handleSSEEvent(event, "System Message", 'System Notification', 'blue');
        };

        // Custom urgent system events
        evtSource.addEventListener("alert", (event) => {
            handleSSEEvent(event, "URGENT SYSTEM ALERT", 'Urgent System Alert', 'red');
        });

        // Listen for document_updated events
        evtSource.addEventListener("document_updated", (event) => {
            handleSSEEvent(event, "Document Updated", 'Document Updated', 'blue');
        });

        // Listen for document_created events
        evtSource.addEventListener("document_created", (event) => {
            handleSSEEvent(event, "Document Created", 'Document Created', 'green');
        });

        // Listen for task_notification events
        evtSource.addEventListener("task_notification", (event) => {
            handleSSEEvent(event, "Task Notification", 'Task Notification', 'green');
        });

        // General generic notification handler
        evtSource.addEventListener("notification", (event) => {
            handleSSEEvent(event, "Notification", 'Notification', 'blue');
        });

        evtSource.onerror = (error) => {
            console.error("SSE connection error:", error);
        };

        return () => {
            evtSource.close();
        };
    }, [token, permission]);

    if (token && "Notification" in window && permission !== "granted") {
        return (
            <Center style={{ height: '100vh', flexDirection: 'column' }}>
                <Text size="xl" mb="md" fw={700}>Notification Access Required</Text>
                <Text mb="lg">You must allow notifications to use this application.</Text>
                {permission === "denied" && (
                    <Text c="red" mb="lg">
                        You have blocked notifications. Please enable them in your browser settings and refresh the page.
                    </Text>
                )}
                {permission === "default" && (
                    <Button onClick={() => Notification.requestPermission().then(setPermission)}>
                        Allow Notifications
                    </Button>
                )}
            </Center>
        );
    }

    return <>{children}</>;
};

export default SSEProvider;
